import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SECTIONS, findSection } from "./navConfig";
import useIsAdmin from "../../customHooks/use-is-admin";

const MUTED = "#64748B";

export default function LeftRail() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const activeSection = findSection(location.pathname);
  const isAdmin = useIsAdmin();
  // Reports / Team / Settings are admin-only. Hide them until the user is
  // confirmed admin so a regular member never sees them (isAdmin is null while
  // resolving, so admin-only options stay hidden during that brief window).
  const sections = SECTIONS.filter((s) => !s.adminOnly || isAdmin === true);

  return (
    <motion.nav
      aria-label="Primary"
      data-tour="primary-nav"
      animate={{ width: collapsed ? 72 : 240 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={{
        flexShrink: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#FFFFFF",
        borderRight: "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        position: "relative",
        zIndex: 40,
      }}
    >
      {/* Brand */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "flex-start", padding: "16px 16px", borderBottom: "1px solid rgba(0,0,0,0.08)", minHeight: 34 }}>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 700, color: "#1A2332", lineHeight: 1.2, whiteSpace: "nowrap" }}>
          {collapsed ? (
            <span style={{ background: "linear-gradient(135deg, #4FC5BF, #7B5AC4, #D946A8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              AH
            </span>
          ) : (
            <>
              AmpleHire{" "}
              <span style={{ fontWeight: 700, fontSize: 14, background: "linear-gradient(135deg, #4FC5BF, #7B5AC4, #D946A8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                AI
              </span>
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 8px" }}>
        {sections.map((section) => {
          const Icon = section.Icon;
          const isActive = activeSection?.key === section.key;
          return (
            <NavLink
              key={section.key}
              to={section.to}
              data-tour={section.tourId || `nav-${section.key}`}
              style={{ textDecoration: "none", display: "block" }}
            >
              <motion.div
                whileHover={{ x: 2 }}
                whileTap={{ scale: 0.98 }}
                title={collapsed ? section.label : undefined}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  borderRadius: 8,
                  marginBottom: 2,
                  cursor: "pointer",
                  transition: "background 200ms, color 200ms",
                  backgroundColor: isActive ? `${section.color}15` : "transparent",
                  color: isActive ? section.color : MUTED,
                }}
              >
                {isActive && (
                  <motion.div
                    layoutId="sidebar-active"
                    style={{ position: "absolute", left: 0, top: 0, bottom: 0, margin: "auto 0", width: 3, height: 24, borderRadius: "0 4px 4px 0", backgroundColor: section.color }}
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
                <Icon size={18} style={{ flexShrink: 0, color: isActive ? section.color : MUTED }} />
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      style={{ fontSize: 13, fontWeight: isActive ? 600 : 500, whiteSpace: "nowrap", overflow: "hidden" }}
                    >
                      {section.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
            </NavLink>
          );
        })}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "12px 0",
          border: "none",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          background: "transparent",
          color: MUTED,
          cursor: "pointer",
          transition: "color 200ms",
        }}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>
    </motion.nav>
  );
}
